import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, MagnifyingGlass, FileText, Sparkle, SortAscending, SortDescending } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { GlassCard } from "@/components/glass/GlassCard";
import { PageShell } from "@/components/layout/AppLayout";
import api from "@/lib/api";

function money(v, ccy = "USD") {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: ccy, maximumFractionDigits: 0 }).format(v || 0);
}

export default function Repository() {
  const nav = useNavigate();
  const [contracts, setContracts] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(true);
  const [semanticMode, setSemanticMode] = useState(false);
  const [semanticHits, setSemanticHits] = useState([]);
  const [sortBy, setSortBy] = useState("updated_at");
  const [sortDir, setSortDir] = useState("desc");

  const load = async () => {
    setLoading(true);
    const params = {};
    if (status !== "all") params.status = status;
    if (q && !semanticMode) params.search = q;
    const { data } = await api.get("/contracts", { params });
    setContracts(data);
    // Re-run semantic when query present
    if (semanticMode && q.trim()) {
      try {
        const { data: sem } = await api.get("/search/semantic", { params: { q, k: 20 } });
        setSemanticHits(sem.results || []);
      } catch (e) {
        setSemanticHits([]);
      }
    } else {
      setSemanticHits([]);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, [status, semanticMode]); // eslint-disable-line
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [q]); // eslint-disable-line

  // When semantic mode is on and we have hits, intersect/order contracts by semantic score
  const displayedContracts = (() => {
    if (semanticMode && q.trim()) {
      if (!semanticHits.length) return [];
      const scoreById = Object.fromEntries(semanticHits.map((h) => [h.contract_id, h]));
      const filtered = contracts.filter((c) => scoreById[c.id]);
      return filtered.sort((a, b) => (scoreById[b.id]?.score || 0) - (scoreById[a.id]?.score || 0));
    }
    // Local sort (client-side) — server returns created_at desc by default
    const dir = sortDir === "asc" ? 1 : -1;
    return [...contracts].sort((a, b) => {
      const av = a[sortBy]; const bv = b[sortBy];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
      return String(av).toLowerCase().localeCompare(String(bv).toLowerCase()) * dir;
    });
  })();

  return (
    <PageShell
      title="Contract Repository"
      subtitle="All contracts across your workspace"
      right={
        <Button onClick={() => nav("/app/studio")} data-testid="repo-new-contract-btn" className="gap-2">
          <Plus size={14} /> New contract
        </Button>
      }
    >
      <GlassCard className="p-4 mb-6 flex flex-col md:flex-row gap-3 items-stretch md:items-center">
        <div className="relative flex-1">
          {semanticMode ? (
            <Sparkle size={16} weight="duotone" className="absolute start-3 top-1/2 -translate-y-1/2 text-[var(--accent)]" />
          ) : (
            <MagnifyingGlass size={16} className="absolute start-3 top-1/2 -translate-y-1/2 text-[var(--text-3)]" />
          )}
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={semanticMode ? "Ask anything — 'contracts with early termination penalties'" : "Search by title, counterparty, tag…"}
            data-testid="repo-search-input"
            className="ps-9 glass-subtle border-[var(--border)]"
          />
        </div>
        <Button
          variant={semanticMode ? "default" : "outline"}
          size="sm"
          onClick={() => setSemanticMode((v) => !v)}
          data-testid="repo-semantic-toggle"
          className="gap-2 shrink-0"
        >
          <Sparkle size={14} weight={semanticMode ? "fill" : "duotone"} />
          {semanticMode ? "Semantic on" : "Semantic"}
        </Button>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="repo-status-filter" className="w-full md:w-52 glass-subtle border-[var(--border)]">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="in_review">In review</SelectItem>
            <SelectItem value="awaiting_signature">Awaiting signature</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="expired">Expired</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger data-testid="repo-sort-by" className="w-full md:w-44 glass-subtle border-[var(--border)]">
            <SelectValue placeholder="Sort by…" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="updated_at">Last updated</SelectItem>
            <SelectItem value="created_at">Date created</SelectItem>
            <SelectItem value="title">Name A→Z</SelectItem>
            <SelectItem value="counterparty">Counterparty</SelectItem>
            <SelectItem value="value_amount">Value</SelectItem>
            <SelectItem value="risk_level">Risk level</SelectItem>
            <SelectItem value="status">Status</SelectItem>
            <SelectItem value="expiration_date">Expiration</SelectItem>
          </SelectContent>
        </Select>
        <Button
          type="button" size="icon" variant="outline"
          onClick={() => setSortDir(sortDir === "asc" ? "desc" : "asc")}
          aria-label={`Sort ${sortDir === "asc" ? "descending" : "ascending"}`}
          data-testid="repo-sort-dir"
          className="h-10 w-10 shrink-0"
        >
          {sortDir === "asc" ? <SortAscending size={14} /> : <SortDescending size={14} />}
        </Button>
      </GlassCard>

      {semanticMode && q.trim() && semanticHits.length > 0 && (
        <GlassCard className="p-3 mb-4 flex items-center gap-2 text-xs text-[var(--text-2)]" data-testid="repo-semantic-banner">
          <Sparkle size={14} weight="duotone" className="text-[var(--accent)]" />
          <span>Found {semanticHits.length} semantic matches for <em>"{q}"</em> — ranked by relevance.</span>
        </GlassCard>
      )}

      {loading ? (
        <div className="text-center text-[var(--text-3)] py-20 text-sm">Loading…</div>
      ) : displayedContracts.length === 0 ? (
        <div className="text-center text-[var(--text-3)] py-20 text-sm">
          {semanticMode && q.trim() ? "No semantic matches — try a broader query or turn off semantic mode." : "No contracts yet. Create one to get started."}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {displayedContracts.map((c) => {
            const hit = semanticHits.find((h) => h.contract_id === c.id);
            return (
            <GlassCard
              key={c.id}
              hover
              as="button"
              data-testid={`repo-card-${c.id}`}
              onClick={() => nav(`/app/repository/${c.id}`)}
              className="p-6 text-left"
            >
              <div className="flex items-start justify-between mb-3">
                <FileText size={22} weight="duotone" className="text-[var(--accent)]" />
                <span className={`chip chip-${c.risk_level || "unknown"}`}>{c.risk_level || "—"}</span>
              </div>
              <div className="text-xs text-[var(--text-3)] mb-1">{c.counterparty}</div>
              <div className="text-lg font-medium leading-tight mb-3 line-clamp-2" style={{ fontFamily: 'var(--font-heading)' }}>
                {c.title}
              </div>
              {hit?.snippet && (
                <div className="text-[11px] text-[var(--text-2)] mb-3 line-clamp-3 italic leading-snug">
                  "{hit.snippet}"
                </div>
              )}
              <div className="flex items-center justify-between text-xs text-[var(--text-3)] pt-3 border-t border-[var(--border)]">
                <span className="chip chip-neutral capitalize">{c.status?.replace("_"," ")}</span>
                <span className="font-mono">{money(c.value_amount, c.value_currency)}</span>
              </div>
            </GlassCard>
            );
          })}
        </div>
      )}
    </PageShell>
  );
}
